
import React from "react";
import { Helmet } from "react-helmet-async";
import { HelpCircle } from "lucide-react";

function Disclaimer() {
  const disclaimerSections = [
    "All trademarks, logos, brand names, product names, and company names mentioned or displayed on this website are the property of their respective owners.",

    "Databricks and the Databricks logo are trademarks of Databricks, Inc. Claude and Anthropic are trademarks of Anthropic, PBC. ServiceNow and the ServiceNow logo are trademarks of ServiceNow, Inc.",

    "All other trademarks, logos, service marks, and client names referenced on this website belong to their respective owners and are used solely for identification, informational, and reference purposes.",

    "Unless explicitly stated, the use or mention of any third-party trademark, logo, brand, or company name does not imply any affiliation, sponsorship, endorsement, or partnership with the respective owner. Any references to clients or partners are intended to reflect only the actual business relationships expressly stated.",

    "We respect the intellectual property rights of all third parties and do not claim ownership of any third-party trademarks, logos, or brand assets displayed on this website."
  ];

  return (
    <>
      <Helmet>
        <title>Disclaimer | Third-Party Trademarks & Intellectual Property | Mobikode</title>

        <meta
          name="description"
          content="Read Mobikode's Disclaimer regarding third-party trademarks, logos, brand names, intellectual property rights, affiliations, sponsorships, and partnerships."
        />

        <meta
          name="keywords"
          content="Mobikode Disclaimer, Trademark Disclaimer, Third Party Trademarks, Intellectual Property Rights, Databricks Trademark, Claude Trademark, Anthropic Trademark, ServiceNow Trademark, Brand Disclaimer"
        />

        <link
          rel="canonical"
          href="https://www.mobikode.com/disclaimer"
        />

        <meta
          property="og:title"
          content="Disclaimer | Third-Party Trademarks & Intellectual Property | Mobikode"
        />

        <meta
          property="og:description"
          content="Learn about Mobikode's disclaimer concerning third-party trademarks, logos, brand assets, and intellectual property rights."
        />

        <meta
          property="og:url"
          content="https://www.mobikode.com/disclaimer"
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:image"
          content="https://www.mobikode.com/assets/what/img1.png"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Disclaimer | Mobikode"
        />

        <meta
          name="twitter:description"
          content="Review Mobikode's disclaimer regarding third-party trademarks, logos, brands, and intellectual property."
        />

        <meta
          name="twitter:image"
          content="https://www.mobikode.com/assets/what/img1.png"
        />
      </Helmet>

      <div className="min-h-screen bg-white text-[#0E2A62]">

        {/* HERO SECTION */}
        <section className="relative w-full h-[450px] lg:h-[550px] flex items-center bg-[#000814] overflow-hidden">

          <div className="absolute inset-0 z-0">
            <img
              src="/assets/what/img1.png"
              alt="Disclaimer Background"
              className="w-full h-full object-cover opacity-80"
            />

            <div
              style={{
                background:
                  "linear-gradient(86.84deg, #000000 20%, rgba(0, 0, 0, 0) 79%)",
              }}
              className="absolute inset-0"
            />
          </div>

          <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

            <h1 className="text-white font-[600] text-4xl md:text-[55px] font-sora leading-[1.1] mb-4">
              Disclaimer
            </h1>

            <p className="text-gray-300 text-base md:text-lg font-graphik font-light max-w-[700px]">
              Transparency, trust, and respect for intellectual property rights.
            </p>

          </div>
        </section>

        {/* DISCLAIMER CONTENT */}
        <section className="py-12 lg:py-20 bg-white">

          <div className="max-w-[1000px] mx-auto px-[20px]">

            {/* INTRODUCTION */}
            <p className="text-[#49577A] text-[14px] lg:text-[16px] font-graphik font-[500] mb-12 leading-relaxed border-l-4 border-[#0E2A62] pl-6 text-justify">
              All trademarks, logos, brand names, product names, and company names mentioned or displayed on this website are the property of their respective owners.
            </p>

            {/* DISCLAIMER PARAGRAPHS */}
            <div className="space-y-8 lg:space-y-10">

              {disclaimerSections.slice(1).map((paragraph, index) => (
                <div
                  key={index}
                  className="border-b border-gray-100 pb-8 last:border-0"
                >
                  <p className="text-[#49577A] text-[14px] md:text-[16px] font-graphik font-[500] leading-relaxed text-justify">
                    {paragraph}
                  </p>
                </div>
              ))}

            </div>

            {/* CONTACT CARD */}
            <div className="mt-16 p-6 md:p-10 bg-[#0E2A62] text-white flex flex-col md:flex-row items-center gap-6 shadow-xl">

              <div className="bg-white/10 p-3 rounded-xl">
                <HelpCircle className="text-white" size={32} />
              </div>

              <div className="flex-1 text-center md:text-left">

                <h4 className="text-[16px] lg:text-[20px] font-sora font-[600] mb-1">
                  Have Questions?
                </h4>

                <p className="text-white/80 font-graphik text-[14px] md:text-[16px] font-[500]">
                  For any clarification, reach out to us at{" "}
                  <strong>info@mobikode.com</strong>
                </p>

              </div>

              <a
                href="mailto:info@mobikode.com"
                className="whitespace-nowrap bg-white text-[#0E2A62] px-8 py-3 rounded-full font-[500] hover:bg-gray-100 transition-all text-[14px] lg:text-[16px] font-graphik"
              >
                Contact Us
              </a>

            </div>

          </div>
        </section>

      </div>
    </>
  );
}

export default Disclaimer;
